import type {
  PlaycastMessage,
  PlaycastMessageTarget,
  GamingScorePersistedData,
  SubmitScoringMetricsPayload,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import { RawMetricsStore } from './stores';
import { MetricsIngestionManager } from './managers/MetricsIngestionManager';
import { MetricsSubmissionManager } from './managers/MetricsSubmissionManager';

const POLL_INTERVAL_MS = 60_000;
const POST_SUBMIT_POLL_DELAY_MS = 15_000;

type EventMap = {
  rawMetricsReceived: { clientTimestamp: number; allMetricsKeys: string[] };
  metricsSubmitted: { clientTimestamp: number; via: 'ws' };
  authoritativeScoreReceived: { score: GamingScorePersistedData };
};

type EventName = keyof EventMap;
type Listener<E extends EventName> = (payload: EventMap[E]) => void;

/**
 * Host-frontend coordinator: forwards Playjector IPC metrics over WS and
 * keeps an up-to-date authoritative score by polling the REST endpoint.
 * Never computes a score — that lives on the server.
 */
export class GamingScoreCoordinator {
  private readonly rawMetricsStore = new RawMetricsStore();
  private readonly ingestion: MetricsIngestionManager;
  private readonly submission: MetricsSubmissionManager;
  private readonly listeners: { [E in EventName]: Set<Listener<E>> } = {
    rawMetricsReceived: new Set(),
    metricsSubmitted: new Set(),
    authoritativeScoreReceived: new Set(),
  };
  private latestScore: GamingScorePersistedData | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private postSubmitTimer: ReturnType<typeof setTimeout> | null = null;
  private pollInFlight = false;
  private disposed = false;

  constructor(
    realtime: IRealtimeService,
    private readonly getMachineId: () => string | null,
    private readonly fetchScore: (machineId: string) => Promise<GamingScorePersistedData | null>,
  ) {
    this.submission = new MetricsSubmissionManager(realtime, payload =>
      this.onMetricsSubmitted(payload),
    );
    this.ingestion = new MetricsIngestionManager(this.rawMetricsStore, payload =>
      this.onRawMetricsIngested(payload),
    );
  }

  init(): void {
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
    this.poll();
  }

  dispose(): void {
    this.disposed = true;
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.postSubmitTimer !== null) {
      clearTimeout(this.postSubmitTimer);
      this.postSubmitTimer = null;
    }
  }

  ingestMetricsFromIpc(message: PlaycastMessage<PlaycastMessageTarget>): void {
    this.ingestion.ingestFromIpc(message);
  }

  getLatestScore(): GamingScorePersistedData | null {
    return this.latestScore;
  }

  hasAuthoritative(): boolean {
    return this.latestScore !== null;
  }

  getRawMetricsTimestamp(): number | null {
    return this.rawMetricsStore.timestamp();
  }

  on<E extends EventName>(event: E, listener: Listener<E>): () => void {
    this.listeners[event].add(listener);
    return () => this.listeners[event].delete(listener);
  }

  private async poll(): Promise<void> {
    if (this.disposed || this.pollInFlight) return;
    const machineId = this.getMachineId();
    if (!machineId) return;

    this.pollInFlight = true;
    try {
      const score = await this.fetchScore(machineId);
      if (this.disposed) return;
      if (score) {
        this.latestScore = score;
        console.debug('[gaming-score] authoritative score received from realtime-api', {
          stage: 'host_poll_received',
          machineId,
          gamingScore: score.gamingScore,
          qualificationLevel: (score.fullData as any)?.qualificationLevel ?? 'unknown',
          lastUpdated: score.lastUpdated,
          calculatedAt: score.calculatedAt,
          driverStatusPresent: score.driverStatus != null,
        });
        this.emit('authoritativeScoreReceived', { score });
      }
    } catch (err) {
      console.warn('[GamingScoreCoordinator] REST poll failed', err);
    } finally {
      this.pollInFlight = false;
    }
  }

  private onRawMetricsIngested(payload: SubmitScoringMetricsPayload): void {
    console.debug('[gaming-score] metrics received from Playjector IPC', {
      stage: 'host_ipc_received',
      machineId: this.getMachineId() ?? 'unknown',
      allMetricsKeyCount: Object.keys(payload.allMetrics ?? {}).length,
      allMetricsKeys: Object.keys(payload.allMetrics ?? {}),
      driverStatusPresent: payload.driverStatus != null,
      machineIdTimestamp: payload.machineIdTimestamp,
      clientTimestamp: payload.clientTimestamp,
    });
    this.emit('rawMetricsReceived', {
      clientTimestamp: payload.clientTimestamp,
      allMetricsKeys: Object.keys(payload.allMetrics ?? {}),
    });
    this.submission.submit(payload);
  }

  private onMetricsSubmitted(payload: SubmitScoringMetricsPayload): void {
    console.debug('[gaming-score] metrics submitted to realtime-api via WebSocket', {
      stage: 'host_ws_submitted',
      machineId: this.getMachineId() ?? 'unknown',
      via: 'ws',
      machineIdTimestamp: payload.machineIdTimestamp,
      clientTimestamp: payload.clientTimestamp,
    });
    this.emit('metricsSubmitted', { clientTimestamp: payload.clientTimestamp, via: 'ws' });
    if (this.postSubmitTimer !== null) clearTimeout(this.postSubmitTimer);
    this.postSubmitTimer = setTimeout(() => {
      this.postSubmitTimer = null;
      this.poll();
    }, POST_SUBMIT_POLL_DELAY_MS);
  }

  private emit<E extends EventName>(event: E, payload: EventMap[E]): void {
    for (const listener of this.listeners[event]) {
      try {
        listener(payload);
      } catch (err) {
        console.warn(`[GamingScoreCoordinator] listener threw for ${event}`, err);
      }
    }
  }
}
